import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { braipApi } from '../services/api';
import type {
  BraipConfigStatus,
  BraipConfigRequest,
  BraipWebhookHistoryResponse,
  BraipStats,
  BraipTestTokenRequest,
  BraipTestTokenResponse,
} from '../types/braip';

/**
 * Hook customizado para gerenciar integração Braip
 * Fornece métodos para configuração, histórico e estatísticas
 */

export const queryKeys = {
  braipConfig: ['braip', 'config'],
  braipWebhookHistory: ['braip', 'webhook-history'],
  braipStats: ['braip', 'stats'],
  braipProducts: ['braip', 'products'],
} as const;

/**
 * Hook para obter configuração Braip
 */
export function useBraipConfig(userId?: string) {
  return useQuery({
    queryKey: [...queryKeys.braipConfig, userId],
    queryFn: async () => {
      const response = await braipApi.getConfig(userId);
      return response.data;
    },
    staleTime: 0, // Sempre buscar dados atualizados (desenvolvimento)
    gcTime: 0, // Não manter em cache (React Query v5)
    retry: 2,
  });
}

/**
 * Hook para salvar configuração Braip
 */
export function useSaveBraipConfig(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: BraipConfigRequest) => {
      const response = await braipApi.saveConfig(config, userId);
      return response.data;
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: queryKeys.braipConfig });
      queryClient.invalidateQueries({ queryKey: queryKeys.braipStats });
    },
  });
}

/**
 * Hook para testar token Braip
 */
export function useTestBraipToken(userId?: string) {
  return useMutation({
    mutationFn: async (request: BraipTestTokenRequest) => {
      const response = await braipApi.testToken(request, userId);
      return response.data as BraipTestTokenResponse;
    },
  });
}

/**
 * Hook para deletar configuração Braip
 */
export function useDeleteBraipConfig(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await braipApi.deleteConfig(userId);
      return response.data;
    },
    onSuccess: () => {
      // Invalidar todas as queries relacionadas
      queryClient.invalidateQueries({ queryKey: queryKeys.braipConfig });
      queryClient.invalidateQueries({ queryKey: queryKeys.braipStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.braipWebhookHistory });
    },
  });
}

/**
 * Hook para obter histórico de webhooks
 */
export function useBraipWebhookHistory(userId?: string, limit: number = 50) {
  return useQuery({
    queryKey: [...queryKeys.braipWebhookHistory, userId, limit],
    queryFn: async () => {
      const response = await braipApi.getWebhookHistory(userId, limit);
      return response.data as BraipWebhookHistoryResponse;
    },
    staleTime: 10000, // 10 segundos
    retry: 2,
  });
}

/**
 * Hook para limpar histórico de webhooks
 */
export function useClearBraipWebhookHistory(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await braipApi.clearWebhookHistory(userId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.braipWebhookHistory });
    },
  });
}

/**
 * Hook para obter estatísticas Braip
 */
export function useBraipStats(userId?: string) {
  return useQuery({
    queryKey: [...queryKeys.braipStats, userId],
    queryFn: async () => {
      const response = await braipApi.getStats(userId);
      return response.data as BraipStats;
    },
    staleTime: 30000, // 30 segundos
    retry: 2,
  });
}

/**
 * Hook para obter produtos Braip (preparado para futuro)
 */
export function useBraipProducts(userId?: string) {
  return useQuery({
    queryKey: [...queryKeys.braipProducts, userId],
    queryFn: async () => {
      const response = await braipApi.getProducts(userId);
      return response.data;
    },
    staleTime: 60000, // 1 minuto
    retry: 1,
    enabled: false, // Desabilitado por padrão (não implementado ainda)
  });
}

/**
 * Hook combinado com todos os dados Braip
 * Útil para páginas que precisam de múltiplas informações
 */
export function useBraipData(userId?: string) {
  const config = useBraipConfig(userId);
  const stats = useBraipStats(userId);
  const webhookHistory = useBraipWebhookHistory(userId, 20);

  return {
    config: config.data,
    stats: stats.data,
    webhookHistory: webhookHistory.data,
    isLoading: config.isLoading || stats.isLoading || webhookHistory.isLoading,
    isError: config.isError || stats.isError || webhookHistory.isError,
    error: config.error || stats.error || webhookHistory.error,
  };
}
